// src/utils/analytics.js
//
// Storefront analytics tracking for TheScope180.
// Writes visit / course_view / checkout_started events to Firestore so the
// weekly store report and TheScopeAI's get_store_stats tool can compute
// historical visitor + sales-funnel numbers (RTDB only holds live presence).
//
// All writes are best-effort and never throw into the UI.

import {
  collection,
  addDoc,
  doc,
  setDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import { isLikelyBot } from "./botDetect";

// Reuse a stable per-browser visitor id (separate from the auth uid). Shares
// the presence tracker's id (useVisitorPresence) so live presence, visit
// history, and the weekly report all refer to the same visitor.
function getVisitorId() {
  try {
    let id = localStorage.getItem("s180_visitor_id") || localStorage.getItem("ts180_visitor_id");
    if (!id) {
      id = "v_" + Math.random().toString(36).slice(2, 11) + Date.now().toString(36);
    }
    localStorage.setItem("s180_visitor_id", id);
    return id;
  } catch {
    return "v_anon";
  }
}

async function safe(fn) {
  try {
    await fn();
  } catch {
    // analytics is non-critical
  }
}

/**
 * One visit per browser session. Sent to /api/visit (trackVisit Cloud Function),
 * which records the visitor, flags new vs returning, adds approximate location,
 * and logs the siteEvents "visit" used by the weekly report.
 */
export function recordVisit({ userEmail = null, userName = null } = {}) {
  if (isLikelyBot()) return;
  try {
    if (sessionStorage.getItem("ts180_visit_logged")) return;
    sessionStorage.setItem("ts180_visit_logged", "1");
  } catch {
    /* sessionStorage unavailable — still record once per load */
  }

  safe(async () => {
    const geo = await browserGeo();
    await fetch("/api/visit", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body: JSON.stringify({
        visitorId: getVisitorId(),
        site: "thescope180.com",
        page: (typeof window !== "undefined" && window.location?.pathname) || "/",
        referrer: (typeof document !== "undefined" && document.referrer) || null,
        userEmail,
        userName,
        automated: navigator.webdriver === true,
        ...geo,
      }),
    });
  });
}

/** Approximate city/region/country for this browser (ipapi.co, 2.5s timeout, cached per session). */
async function browserGeo() {
  try {
    const cached = sessionStorage.getItem("ts180_geo");
    if (cached) return JSON.parse(cached);
  } catch {
    /* ignore */
  }
  try {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 2500);
    const resp = await fetch("https://ipapi.co/json/", { signal: ctrl.signal });
    clearTimeout(timer);
    const g = resp.ok ? await resp.json() : {};
    const geo = g && !g.error ? { city: g.city || null, region: g.region || null, country: g.country_name || null } : {};
    try { sessionStorage.setItem("ts180_geo", JSON.stringify(geo)); } catch { /* ignore */ }
    return geo;
  } catch {
    return {};
  }
}

/** A learner viewed a course / product (funnel step 1). */
export function recordCourseView({ id, title, price } = {}) {
  safe(async () => {
    await addDoc(collection(db, "siteEvents"), {
      type: "product_view",
      visitorId: getVisitorId(),
      productId: id || null,
      productName: title || "Course",
      value: Number(price) || 0,
      createdAt: serverTimestamp(),
      ts: Date.now(),
    });
  });
}

/** A checkout was started (funnel step 2). Opens an abandoned-cart record. */
export function recordCheckoutStarted({ title, price, productId, userEmail } = {}) {
  safe(async () => {
    const visitorId = getVisitorId();
    const value = Number(price) || 0;

    await addDoc(collection(db, "siteEvents"), {
      type: "checkout_started",
      visitorId,
      productId: productId || null,
      productName: title || "Checkout",
      value,
      userEmail: userEmail || null,
      createdAt: serverTimestamp(),
      ts: Date.now(),
    });

    await setDoc(doc(db, "abandonedCarts", visitorId), {
      visitorId,
      status: "open",
      value,
      item: title || null,
      userEmail: userEmail || null,
      startedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  });
}

/** Checkout completed — mark any open cart for this visitor as recovered. */
export function recordCheckoutCompleted(value = null, userEmail = null) {
  safe(async () => {
    const visitorId = getVisitorId();
    // No read: carts are write-only for visitors. Fails harmlessly when no cart exists.
    await updateDoc(doc(db, "abandonedCarts", visitorId), {
      status: "recovered",
      recoveredAt: serverTimestamp(),
      ...(typeof value === "number" ? { recoveredValue: value } : {}),
      updatedAt: serverTimestamp(),
      ...(userEmail ? { userEmail } : {}),
    });
  });
}
